import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/client/lib/api-client'

// Types
export type ReleaseChannel = 'stable' | 'beta'

export interface VersionInfo {
  release_date: string
  requires_php?: string
  requires_wp?: string
  changelog?: string
  downloads?: number
  channel?: ReleaseChannel
}

export interface Theme {
  slug: string
  name: string
  type: 'theme'
  current_version: string
  requires_license: boolean
  author: string
  homepage?: string
  requires_php: string
  requires_wp: string
  tested_wp: string
  last_updated: string
  parent_theme?: string
  screenshot?: string
  framework?: string
  framework_version?: string
  demo_url?: string
  sections: {
    description: string
    installation?: string
    changelog: string
    faq?: string
  }
  versions: Record<string, VersionInfo>
  channels?: {
    stable: string
    beta?: string
  }
  total_downloads?: number
}

export interface CreateThemeData {
  slug: string
  name: string
  current_version: string
  requires_license?: boolean
  author?: string
  requires_php?: string
  requires_wp?: string
  tested_wp?: string
  parent_theme?: string
  screenshot?: string
  framework?: string
  demo_url?: string
  sections?: {
    description?: string
    changelog?: string
  }
}

export interface UpdateThemeData {
  slug: string
  name?: string
  author?: string
  requires_php?: string
  requires_wp?: string
  tested_wp?: string
  requires_license?: boolean
  parent_theme?: string
  screenshot?: string
  framework?: string
  demo_url?: string
  sections?: {
    description?: string
    changelog?: string
  }
}

// Query keys
export const themeKeys = {
  all: ['themes'] as const,
  lists: () => [...themeKeys.all, 'list'] as const,
  list: () => [...themeKeys.lists()] as const,
  details: () => [...themeKeys.all, 'detail'] as const,
  detail: (slug: string) => [...themeKeys.details(), slug] as const,
}

// Hooks
export function useThemes() {
  return useQuery({
    queryKey: themeKeys.list(),
    queryFn: async () => {
      const response = await apiClient.get<{ themes: Theme[] }>('/api/update-server/themes')
      return response.themes || []
    },
  })
}

export function useTheme(slug: string) {
  return useQuery({
    queryKey: themeKeys.detail(slug),
    queryFn: async () => {
      const response = await apiClient.get<{
        success: boolean
        theme: Theme
        available_versions: string[]
        download_stats: { total: number }
      }>(`/api/update-server/themes/${slug}`)
      return {
        ...response.theme,
        total_downloads: response.download_stats?.total,
      }
    },
    enabled: !!slug,
  })
}

export function useCreateTheme() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateThemeData) => {
      return apiClient.post<Theme>('/api/update-server/themes', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: themeKeys.lists() })
    },
  })
}

export function useUpdateTheme() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateThemeData) => {
      return apiClient.post<Theme>('/api/update-server/themes', data)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: themeKeys.lists() })
      queryClient.invalidateQueries({ queryKey: themeKeys.detail(variables.slug) })
    },
  })
}

export function useDeleteTheme() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (slug: string) => {
      return apiClient.delete(`/api/update-server/themes/${slug}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: themeKeys.lists() })
    },
  })
}

export interface PromoteThemeVersionData {
  slug: string
  version: string
  toChannel: ReleaseChannel
}

export interface PromoteVersionResponse {
  message: string
  version: string
  from_channel: ReleaseChannel
  to_channel: ReleaseChannel
  channels: {
    stable: string
    beta?: string
  }
}

export function usePromoteThemeVersion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ slug, version, toChannel }: PromoteThemeVersionData) => {
      return apiClient.post<PromoteVersionResponse>(
        `/api/update-server/themes/${slug}/promote`,
        { version, to: toChannel }
      )
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: themeKeys.lists() })
      queryClient.invalidateQueries({ queryKey: themeKeys.detail(variables.slug) })
    },
  })
}
